import React, { useEffect, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { Nav, Navbar, NavDropdown, Form } from 'react-bootstrap'
import { getUserDetails, logout } from '../actions/userActions'
import { logoutOwner } from '../actions/ownerActions'
import { getSettings } from '../actions/settingActions'
import {
  EMPLOYEE_PERMISSIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBoxesStacked,
  faCashRegister,
  faChartLine,
  faEllipsis,
  faGaugeHigh
} from '@fortawesome/free-solid-svg-icons'
import { ThemeContext } from './ThemeContext.js'

const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, isDarkMode, setIsDarkMode } = useContext(ThemeContext)

  const navTextStyle = {
    color: theme.text
  }

  const dropdownItemStyle = {
    color: theme.text
  }

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const isAuthenticated = Boolean(userInfo || ownerInfo)
  const isOwner = Boolean(ownerInfo?.isOwner)
  const canManageInventory = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.INVENTORY_MANAGE
  )
  const canViewReports = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.REPORTS_VIEW
  )
  const canResetEmployeePasswords = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.EMPLOYEE_PASSWORD_RESET
  )

  const userDetails = useSelector((state) => state.userDetails)
  const { user } = userDetails

  const settingList = useSelector((state) => state.settingList)
  const { settings, success } = settingList

  const activeDisplayName =
    ownerInfo ?
      ownerInfo.name || 'Owner'
    : userInfo?.name || user?.name || 'User'

  const logoutHandler = async () => {
    if (userInfo) {
      const id = userInfo._id
      localStorage.removeItem('userInfo')
      dispatch(logout(id))
    } else if (ownerInfo) {
      await dispatch(logoutOwner(ownerInfo._id))
      navigate('/owner/login')
    }
  }

  useEffect(() => {
    if (isAuthenticated && !success) {
      dispatch(getSettings())
    } else if (userInfo && success) {
      dispatch(getUserDetails(userInfo._id))
    }
  }, [isAuthenticated, userInfo, success, dispatch])

  return (
    <>
      <Navbar
        className='themed-header px-4 py-3 border-bottom shadow-sm'
        variant={isDarkMode ? 'dark' : 'light'}
        style={{
          '--header-text': theme.text,
          '--header-subtext': theme.subtext,
          '--header-card': theme.card,
          '--header-accent': theme.accent,
          '--header-border': isDarkMode ? '#343D35' : '#E0E6DE',
          '--header-hover-bg': isDarkMode ? '#2B352D' : '#EFF3EE',
          '--header-active-bg': isDarkMode ? '#344137' : '#E6ECE4',
          '--header-top-hover-bg':
            isDarkMode ?
              'rgba(169, 190, 165, 0.12)'
            : 'rgba(102, 125, 104, 0.1)',
          '--header-top-active-bg':
            isDarkMode ?
              'rgba(169, 190, 165, 0.2)'
            : 'rgba(102, 125, 104, 0.16)',
          '--header-focus-ring':
            isDarkMode ?
              'rgba(169, 190, 165, 0.3)'
            : 'rgba(102, 125, 104, 0.28)',
          backgroundColor: theme.card,
          borderColor: isDarkMode ? '#343D35' : '#E0E6DE'
        }}
        expand='lg'
      >
        {!isAuthenticated ?
          <Navbar.Brand
            as={Link}
            to='/'
            className='fw-bold'
            style={{ color: theme.text }}
          >
            <span style={{ color: theme.accent }}>🌿</span> Geranium PoS
          </Navbar.Brand>
        : <Navbar.Brand className='fw-bold' style={{ color: theme.text }}>
            {settings.taxrate !== undefined && (
              <span>
                <FontAwesomeIcon icon={faCashRegister} />{' '}
                {` Tax ${settings.taxrate}% `}
              </span>
            )}
            {settings.currency}{' '}
            {ownerInfo ?
              `Owner: ${activeDisplayName}`
            : `User: ${activeDisplayName}`}
          </Navbar.Brand>
        }

        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        <Navbar.Collapse id='basic-navbar-nav'>
          <Nav className='ms-auto d-flex align-items-center gap-2'>
            <Form.Check
              type='switch'
              id='theme-switch'
              label={isDarkMode ? '🌙 Dark' : '☀️ Light'}
              checked={isDarkMode}
              onChange={() => setIsDarkMode(!isDarkMode)}
              className='small'
              style={{ color: theme.subtext }}
            />

            {!isAuthenticated && (
              <NavDropdown
                title={<span style={{ color: theme.text }}>Menu</span>}
                id='basic-nav-dropdown'
                className='themed-nav-dropdown'
                menuVariant={isDarkMode ? 'dark' : undefined}
              >
                <LinkContainer to='/login'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user'></i> User Login
                  </Nav.Link>
                </LinkContainer>
                {/* <LinkContainer to='/register'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user-shield'></i> User Register
                  </Nav.Link>
                </LinkContainer> */}
                <LinkContainer to='/owner/login'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user-cog'></i> Owner Login
                  </Nav.Link>
                </LinkContainer>
                <LinkContainer to='/owner/register'>
                  <Nav.Link
                    className='themed-dropdown-link'
                    style={dropdownItemStyle}
                  >
                    <i className='fas fa-user-lock'></i> Owner Register
                  </Nav.Link>
                </LinkContainer>
              </NavDropdown>
            )}
            {isAuthenticated && (
              <>
                <LinkContainer to='/pos'>
                  <Nav.Link className='header-primary-link'>
                    <FontAwesomeIcon icon={faCashRegister} aria-hidden='true' />
                    Sales
                  </Nav.Link>
                </LinkContainer>
                <LinkContainer to='/inventory'>
                  <Nav.Link className='header-primary-link'>
                    <FontAwesomeIcon icon={faBoxesStacked} aria-hidden='true' />
                    Inventory
                  </Nav.Link>
                </LinkContainer>
                {canViewReports && (
                  <LinkContainer to='/reports'>
                    <Nav.Link className='header-primary-link'>
                      <FontAwesomeIcon icon={faChartLine} aria-hidden='true' />
                      Reports
                    </Nav.Link>
                  </LinkContainer>
                )}
                {canViewReports && (
                  <LinkContainer to='/dashboard'>
                    <Nav.Link className='header-primary-link'>
                      <FontAwesomeIcon icon={faGaugeHigh} aria-hidden='true' />
                      Dashboard
                    </Nav.Link>
                  </LinkContainer>
                )}
                <NavDropdown
                  title={
                    <span style={navTextStyle}>
                      <FontAwesomeIcon
                        icon={faEllipsis}
                        className='me-2'
                        aria-hidden='true'
                      />
                      More
                    </span>
                  }
                  className='themed-nav-dropdown'
                  menuVariant={isDarkMode ? 'dark' : undefined}
                >
                  <LinkContainer to='/returns'>
                    <NavDropdown.Item
                      className='themed-dropdown-item'
                      style={dropdownItemStyle}
                    >
                      Sales Return
                    </NavDropdown.Item>
                  </LinkContainer>
                  {canManageInventory && (
                    <LinkContainer to='/inventory/add'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Add Inventory
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  <LinkContainer to='/items'>
                    <NavDropdown.Item
                      className='themed-dropdown-item'
                      style={dropdownItemStyle}
                    >
                      Items
                    </NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/name'>
                    <NavDropdown.Item
                      className='themed-dropdown-item'
                      style={dropdownItemStyle}
                    >
                      Manufacturers
                    </NavDropdown.Item>
                  </LinkContainer>
                  {(isOwner || canResetEmployeePasswords) && (
                    <LinkContainer to='/admin/userlist'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        {isOwner ? 'Employees' : 'Reset employee passwords'}
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {isOwner && (
                    <LinkContainer to='/settings'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Settings
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {userInfo && (
                    <LinkContainer to='/profile'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Profile
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  {ownerInfo && (
                    <LinkContainer to='/owner/profile'>
                      <NavDropdown.Item
                        className='themed-dropdown-item'
                        style={dropdownItemStyle}
                      >
                        Profile
                      </NavDropdown.Item>
                    </LinkContainer>
                  )}
                  <NavDropdown.Item
                    className='themed-dropdown-item'
                    as='a'
                    href='mailto:info@hermanosgazpachos.ca'
                    style={dropdownItemStyle}
                  >
                    Contact Us
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    className='themed-dropdown-item'
                    onClick={logoutHandler}
                    style={dropdownItemStyle}
                  >
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </>
  )
}

export default Header
